import { 
    mdiChatOutline, 
    mdiEmailOpenOutline, 
    mdiFileDocumentOutline, 
    mdiForumOutline,
    mdiMagnify 
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState } from 'react';
import { BaseCheckbox, BaseInput, ButtonGroup, IButtonGroupItemProps } from '../../../base';
import { IColumnFilterCustomProps } from '../filters/filters.models';
import * as S from './status-filter.styles';

// Example of a custom column filter. It was planned to be used but currently not used in the project and serves as a documentation example.
export const StatusFilter: React.FC<IColumnFilterCustomProps> = ({ 
    value, 
    onChange
}) => {
    const [filters, setFilters] = useState<any>(value || {});
    const [searchTerm, setSearchTerm] = useState('');

    const handleFilterChange = (key: string, newValue: string) => {
        const updated = { ...filters, [key]: newValue };
        setFilters(updated);
        onChange(updated);
    };

    const handlePriorityToggle = (priority: string) => {
        const currentPriorities = filters.priorities || [];
        const updated = currentPriorities.includes(priority)
            ? currentPriorities.filter((p: string) => p !== priority)
            : [...currentPriorities, priority];
        
        const updatedFilters = { ...filters, priorities: updated };
        setFilters(updatedFilters);
        onChange(updatedFilters);
    };

    const handleReset = () => {
        setFilters({});
        onChange({});
    };

    const handleClearPriority = () => {
        const updated = { ...filters, priorities: [] };
        setFilters(updated);
        onChange(updated);
    };


    const openClosedButtons: IButtonGroupItemProps[] = [
        { label: 'All' },
        { label: 'Open' },
        { label: 'Closed' }
    ];
    const readUnreadButtons: IButtonGroupItemProps[] = [
        { label: 'All' },
        { label: 'Read' },
        { label: 'Unread' }
    ];
    const yesNoButtons: IButtonGroupItemProps[] = [
        { label: 'All' },
        { label: 'Yes' },
        { label: 'No' }
    ];

    const getSelectedButton = (buttons: IButtonGroupItemProps[], value?: string) => {
        if (!value || value === 'All') return buttons[0];
        return buttons.find(b => b.label === value) || buttons[0];
    };

    return (
        <S.StatusFilterContainer>
            <S.Header>
                <div>
                    <S.Title>Status filters</S.Title>
                    <S.Subtitle>Toggle individual indicators or narrow by assignee.</S.Subtitle>
                </div>
                <S.ResetButton onClick={handleReset}>Reset</S.ResetButton>
            </S.Header>

            <S.FilterSection>
                <S.FilterRow>
                    <S.FilterLabel>
                        <S.IconWrapper>
                            <Icon path={mdiForumOutline} size={0.7} />
                        </S.IconWrapper>
                        <div>
                            <S.FilterName>Support thread</S.FilterName>
                            <S.FilterValue>{filters.supportThread || 'All'}</S.FilterValue>
                        </div>
                    </S.FilterLabel>
                    <ButtonGroup
                        buttons={openClosedButtons}
                        selected={getSelectedButton(openClosedButtons, filters.supportThread)}
                        onSelect={(btn) => handleFilterChange('supportThread', btn.label)}
                    />
                </S.FilterRow>

                <S.FilterRow>
                    <S.FilterLabel>
                        <S.IconWrapper>
                            <Icon path={mdiEmailOpenOutline} size={0.7} />
                        </S.IconWrapper>
                        <div>
                            <S.FilterName>Support read</S.FilterName>
                            <S.FilterValue>{filters.supportRead || 'All'}</S.FilterValue>
                        </div>
                    </S.FilterLabel>
                    <ButtonGroup
                        buttons={readUnreadButtons}
                        selected={getSelectedButton(readUnreadButtons, filters.supportRead)}
                        onSelect={(btn) => handleFilterChange('supportRead', btn.label)}
                    />
                </S.FilterRow>

                <S.FilterRow>
                    <S.FilterLabel>
                        <S.IconWrapper>
                            <Icon path={mdiChatOutline} size={0.7} />
                        </S.IconWrapper>
                        <div>
                            <S.FilterName>Provider thread</S.FilterName>
                            <S.FilterValue>{filters.providerThread || 'All'}</S.FilterValue>
                        </div>
                    </S.FilterLabel>
                    <ButtonGroup
                        buttons={openClosedButtons}
                        selected={getSelectedButton(openClosedButtons, filters.providerThread)}
                        onSelect={(btn) => handleFilterChange('providerThread', btn.label)}
                    />
                </S.FilterRow>

                <S.FilterRow>
                    <S.FilterLabel>
                        <S.IconWrapper>
                            <Icon path={mdiEmailOpenOutline} size={0.7} />
                        </S.IconWrapper>
                        <div>
                            <S.FilterName>Provider read</S.FilterName>
                            <S.FilterValue>{filters.providerRead || 'All'}</S.FilterValue>
                        </div>
                    </S.FilterLabel>
                    <ButtonGroup
                        buttons={readUnreadButtons}
                        selected={getSelectedButton(readUnreadButtons, filters.providerRead)}
                        onSelect={(btn) => handleFilterChange('providerRead', btn.label)}
                    />
                </S.FilterRow>

                <S.FilterRow>
                    <S.FilterLabel>
                        <S.IconWrapper>
                            <Icon path={mdiFileDocumentOutline} size={0.7} />
                        </S.IconWrapper>
                        <div>
                            <S.FilterName>Internal note</S.FilterName>
                            <S.FilterValue>{filters.internalNote || 'All'}</S.FilterValue>
                        </div>
                    </S.FilterLabel>
                    <ButtonGroup
                        buttons={yesNoButtons}
                        selected={getSelectedButton(yesNoButtons, filters.internalNote)}
                        onSelect={(btn) => handleFilterChange('internalNote', btn.label)}
                    />
                </S.FilterRow>
            </S.FilterSection>

            <S.PrioritySection>
                <S.PriorityHeader>
                    <S.SectionTitle>Priority</S.SectionTitle>
                    <S.ClearButton onClick={handleClearPriority}>Clear</S.ClearButton>
                </S.PriorityHeader>
                <S.PriorityGrid>
                    <S.PriorityItem>
                        <BaseCheckbox
                            label="Urgent"
                            checked={(filters.priorities || []).includes('URGENT')}
                            onChange={() => handlePriorityToggle('URGENT')}
                        />
                        <S.PriorityTag>High priority</S.PriorityTag>
                    </S.PriorityItem>
                    <S.PriorityItem>
                        <BaseCheckbox
                            label="High"
                            checked={(filters.priorities || []).includes('HIGH')}
                            onChange={() => handlePriorityToggle('HIGH')}
                        />
                        <S.PriorityTag>Elevated</S.PriorityTag>
                    </S.PriorityItem>
                    <S.PriorityItem>
                        <BaseCheckbox
                            label="Normal"
                            checked={(filters.priorities || []).includes('NORMAL')}
                            onChange={() => handlePriorityToggle('NORMAL')}
                        />
                        <S.PriorityTag>Standard</S.PriorityTag>
                    </S.PriorityItem>
                    <S.PriorityItem>
                        <BaseCheckbox
                            label="Low"
                            checked={(filters.priorities || []).includes('LOW')}
                            onChange={() => handlePriorityToggle('LOW')}
                        />
                        <S.PriorityTag>Low touch</S.PriorityTag>
                    </S.PriorityItem>
                    <S.PriorityItem>
                        <BaseCheckbox
                            label="None"
                            checked={(filters.priorities || []).includes('NONE')}
                            onChange={() => handlePriorityToggle('NONE')}
                        />
                        <S.PriorityTag>Not set</S.PriorityTag>
                    </S.PriorityItem>
                </S.PriorityGrid>
            </S.PrioritySection>

            <S.AssigneesSection>
                <S.AssigneesHeader>
                    <S.SectionTitle>Assignees</S.SectionTitle>
                    <S.ClearButton>Clear</S.ClearButton>
                </S.AssigneesHeader>
                <S.SearchWrapper>
                    <S.SearchIcon path={mdiMagnify} size={0.8} />
                    <BaseInput
                        placeholder="Search assignees..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    />
                </S.SearchWrapper>
                <S.AssigneesList>
                    <S.AssigneeItem>
                        <BaseCheckbox />
                        <S.AssigneeAvatar>BG</S.AssigneeAvatar>
                        <S.AssigneeName>Beverly Gueriba</S.AssigneeName>
                    </S.AssigneeItem>
                    <S.AssigneeItem>
                        <BaseCheckbox />
                        <S.AssigneeAvatar>BA</S.AssigneeAvatar>
                        <S.AssigneeName>Business Account</S.AssigneeName>
                    </S.AssigneeItem>
                    <S.AssigneeItem>
                        <BaseCheckbox />
                        <S.AssigneeAvatar>EI</S.AssigneeAvatar>
                        <S.AssigneeName>Enter, Inc.</S.AssigneeName>
                    </S.AssigneeItem>
                </S.AssigneesList>
            </S.AssigneesSection>
        </S.StatusFilterContainer>
    );
};
